import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useCart } from "@/hooks/useCart";
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const CartDrawer = () => {
  const navigate = useNavigate();
  const { items, removeItem, updateQuantity, total, itemCount, isDrawerOpen, setDrawerOpen } = useCart();

  const handleViewCart = () => {
    setDrawerOpen(false);
    navigate("/cart");
  };

  const handleCheckout = () => {
    setDrawerOpen(false);
    navigate("/checkout");
  };

  return (
    <Sheet open={isDrawerOpen} onOpenChange={setDrawerOpen}>
      <SheetContent className="flex flex-col w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <ShoppingBag className="w-5 h-5" />
            Your Cart ({itemCount})
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 text-center px-4">
            <ShoppingBag className="w-16 h-16 text-muted-foreground/30" />
            <div>
              <p className="font-medium text-foreground mb-1">Your cart is empty</p>
              <p className="text-sm text-muted-foreground">Browse our Training Kits or Pricing plans to get started.</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => { setDrawerOpen(false); navigate("/training-kits"); }}>
                Training Kits
              </Button>
              <Button variant="outline" size="sm" onClick={() => { setDrawerOpen(false); navigate("/pricing"); }}>
                Pricing
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto space-y-3 py-4">
              {items.map((item) => (
                <div key={item.id} className="flex gap-3 p-3 rounded-lg border bg-card">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-foreground truncate">{item.name}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="secondary" className="text-xs capitalize">
                        {item.type === "box" ? "Training Kit" : item.type === "subscription" ? "Subscription" : "AI Credits"}
                      </Badge>
                      {item.interval && (
                        <span className="text-xs text-muted-foreground">/{item.interval}</span>
                      )}
                    </div>
                    <p className="font-semibold text-sm mt-2 text-foreground">
                      ${(item.price * item.quantity).toFixed(2)}
                    </p>
                  </div>

                  <div className="flex flex-col items-end justify-between">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-destructive"
                      onClick={() => removeItem(item.id)}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>

                    {item.type === "box" && (
                      <div className="flex items-center gap-1">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          disabled={item.quantity <= 1}
                        >
                          <Minus className="w-3 h-3" />
                        </Button>
                        <span className="text-sm font-medium w-6 text-center text-foreground">{item.quantity}</span>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        >
                          <Plus className="w-3 h-3" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="border-t pt-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="font-medium text-foreground">Subtotal</span>
                <span className="text-lg font-bold text-foreground">${total.toFixed(2)}</span>
              </div>
              <Button className="w-full gap-2" onClick={handleCheckout}>
                Checkout <ArrowRight className="w-4 h-4" />
              </Button>
              <Button variant="outline" className="w-full" onClick={handleViewCart}>
                View Full Cart
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
